import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setMessageAction } from '@/redux/slicers/api-message.slice';
import { setLoadingAction } from '@/redux/slicers/loading.slice';
import { tick } from '@/utils/common';
import { API } from '@/utils/axios';
import { Messages } from '@/constants/message';
import { AxiosRequestConfig, ResponseType } from 'axios';

export function useApi(basePath: string) {
  const axios = API;

  return useMemo(() => {
    return {
      get: (
        path: string,
        query: Record<string, any> = {},
        responseType?: ResponseType,
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.get(url, { ...config, params: query, responseType });
      },
      post: (
        path: string,
        body: Record<string, any> | FormData | null = {},
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.post(url, body, config);
      },
      put: (
        path: string,
        body: Record<string, any> | FormData = {},
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.put(url, body, config);
      },
      del: (
        path: string,
        query: Record<string, any> = {},
        data: Record<string, any> = {},
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http') ? path : `${axios.defaults.baseURL}${basePath}${path}`;
        return axios.delete(url, { ...config, params: query, data: data });
      },
      patch: (
        path: string,
        body: Record<string, any> | FormData = {},
        config?: AxiosRequestConfig,
      ): any => {
        const url = path.startsWith('http')
          ? path
          : `${axios.defaults.baseURL}${basePath}${path}`;

        return axios.patch(url, body, config);
      },

    };
  }, [basePath, axios]);
}

interface CallerOptions {
  messageKey?: string;
  loadingKey?: string;
  successMessage?: string;
  errorMessage?: string;
  delay?: number;
  returnErrorResponse?: boolean;
}

type Caller = <T = any>(
  func: () => Promise<T | null | undefined>,
  options?: CallerOptions,
) => Promise<T | null | undefined>;

function getApiErrorMessages(responseData: any, fallback?: string): string[] {
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    const messages = responseData.errors
      .map((error: unknown) => typeof error === 'string' ? error : (error as { message?: unknown })?.message)
      .filter((message: unknown): message is string => typeof message === 'string' && message.trim().length > 0);
    if (messages.length) return messages;
  }
  if (Array.isArray(responseData?.messages)) {
    const messages = responseData.messages.filter((message: unknown): message is string => typeof message === 'string' && message.trim().length > 0);
    if (messages.length) return messages;
  }
  if (typeof responseData?.message === 'string' && responseData.message.trim()) return [responseData.message];
  if (typeof responseData?.error === 'string' && responseData.error.trim()) return [responseData.error];
  if (fallback) return [fallback];
  return [Messages.API_ERROR];
}

export function useCaller() {
  const dispatch = useDispatch();

  const setMessage = useCallback(
    (key: string, info?: MessageOptions) => {
      dispatch(setMessageAction([key, info]));
    },
    [dispatch],
  );

  const setLoading = useCallback(
    (key: string, el?: boolean) => {
      dispatch(setLoadingAction([key, el]));
    },
    [dispatch],
  );

  const caller = useCallback<Caller>(
    async (
      func,
      {
        successMessage = '',
        errorMessage,
        messageKey = 'global',
        loadingKey = 'global',
        delay,
        returnErrorResponse = false,
      } = {},
    ) => {
      messageKey && setMessage(messageKey);
      try {
        setLoading(loadingKey, true);

        const [res]: any = await Promise.all([func(), delay != null ? tick(delay) : undefined]);

        // Backend response format: { isSuccess, data, errors }
        const responseData = res?.data;

        // Debug logging

        // Check for error conditions
        const isError =
          res == null ||
          responseData == null ||
          responseData.isSuccess === false ||
          res.exception === 'Error' ||
          res.status >= 400 ||
          res.statusCode >= 400;

        if (isError) {
          {
            // Get error messages from response.messages array or use default
            const errorMessages = getApiErrorMessages(responseData, errorMessage);

            setMessage(messageKey || 'global', {
              type: 'error',
              messages: errorMessages,
            });
          }
          setTimeout(() => setLoading(loadingKey, false), 0);
          if (returnErrorResponse && responseData) {
            return responseData;
          }
          return null;
        }

        // Show success message if provided
        if (successMessage !== '') {
          if (messageKey) {
            // Check if there are error messages in response
            const hasErrorMessages =
              responseData?.messages &&
              Array.isArray(responseData.messages) &&
              responseData.messages.length > 0;

            if (hasErrorMessages) {
              setMessage(messageKey, {
                type: 'error',
                messages: responseData.messages,
              });
            } else {
              setMessage(messageKey, {
                type: 'success',
                messages: [successMessage],
              });
            }
          }
        }
        setTimeout(() => setLoading(loadingKey, false), 0);

        return responseData;
      } catch (error: any) {
        const errorResponseData = error?.response?.data;

        {
          const errors = errorResponseData?.message || errorResponseData?.error;
          setMessage(messageKey || 'global', {
            type: 'error',
            messages: getApiErrorMessages(errorResponseData, errorMessage || (typeof errors === 'string' ? errors : undefined)),
          });
        }
        setTimeout(() => setLoading(loadingKey, false), 0);
        if (returnErrorResponse && errorResponseData) {
          return errorResponseData;
        }
        return null;
      }
    },
    [setLoading, setMessage],
  );
  return caller;
}
